import React, { useState, useEffect } from 'react';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
    PieChart, Pie, Cell, Legend 
} from 'recharts';
import { 
    Loader2, 
    Download, 
    FileSpreadsheet, 
    FileText, 
    TrendingUp, 
    Users, 
    CheckCircle, 
    Target 
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../../../services/api';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

export default function Reports() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [year, setYear] = useState(new Date().getFullYear());

    useEffect(() => { fetchStats(); }, [year]);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/reports/stats?year=${year}`);
            setData(res.data);
        } catch {
            toast.error('Failed to load report data');
        } finally {
            setLoading(false);
        }
    };

    const downloadPDF = () => {
        if (!data) return;
        try {
            const doc = new jsPDF();
            const watermark = "WESTLAKE VILLAGE HOSPICE";

            // Add Watermark
            doc.setTextColor(245, 245, 245);
            doc.setFontSize(50);
            doc.text(watermark, 35, 150, { angle: 45 });

            // Content
            doc.setTextColor(30, 82, 139); // Westlake Blue
            doc.setFontSize(22);
            doc.text("CRM Performance Report", 14, 20);
            
            doc.setTextColor(100);
            doc.setFontSize(10);
            doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 28);
            doc.text(`Reporting Year: ${year}`, 14, 34);

            // Summary Table
            autoTable(doc, {
                startY: 45,
                head: [['Indicator', 'Value']],
                body: [
                    ['Total Leads', data.summary.total],
                    ['Converted Leads', data.summary.converted],
                    ['Conversion Rate', `${data.summary.conversion_rate}%`],
                    ['New Leads', data.summary.new],
                    ['Lost Leads', data.summary.lost],
                ],
                theme: 'striped',
                headStyles: { fillStyle: [30, 82, 139] }
            });

            // Source Table
            autoTable(doc, {
                startY: doc.lastAutoTable.finalY + 15,
                head: [['Referral Source', 'Lead Count']],
                body: data.sources.map(s => [s.source, s.value]),
                theme: 'grid',
                headStyles: { fillStyle: [16, 185, 129] }
            });

            doc.save(`Westlake_CRM_Report_${year}.pdf`);
        } catch (error) {
            console.error(error);
            toast.error('Failed to generate PDF report');
        }
    };

    const downloadExcel = async () => {
        if (!data) return;
        const workbook = new ExcelJS.Workbook();
        
        // Summary Sheet
        const wsSummary = workbook.addWorksheet('Overview');
        wsSummary.addRow(["CRM PERFORMANCE REPORT", year]);
        wsSummary.addRow(["Generated at", new Date().toLocaleString()]);
        wsSummary.addRow([]);
        wsSummary.addRow(["Metric", "Value"]);
        wsSummary.addRow(["Total Leads", data.summary.total]);
        wsSummary.addRow(["Converted Leads", data.summary.converted]);
        wsSummary.addRow(["Conversion Rate", `${data.summary.conversion_rate}%`]);
        wsSummary.addRow(["New Leads", data.summary.new]);
        wsSummary.addRow(["Lost Leads", data.summary.lost]);

        // Monthly Data
        const wsMonthly = workbook.addWorksheet('Monthly Trends');
        if (data.monthly && data.monthly.length > 0) {
            wsMonthly.columns = Object.keys(data.monthly[0]).map(k => ({ header: k, key: k }));
            data.monthly.forEach(row => wsMonthly.addRow(row));
        }

        // Sources
        const wsSources = workbook.addWorksheet('Referral Sources');
        if (data.sources && data.sources.length > 0) {
            wsSources.columns = Object.keys(data.sources[0]).map(k => ({ header: k, key: k }));
            data.sources.forEach(row => wsSources.addRow(row));
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Westlake_CRM_Export_${year}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    if (loading) return <div className="flex justify-center items-center min-h-[60vh]"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>;
    if (!data) return null;

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <Toaster position="top-right" richColors />
            
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 tracking-tight">CRM Analytics</h2>
                    <p className="text-gray-500">Professional performance reporting and insights</p>
                </div>
                
                <div className="flex items-center gap-3">
                    <select 
                        value={year} 
                        onChange={(e) => setYear(e.target.value)}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none shadow-sm"
                    >
                        {[0, 1, 2].map(offset => {
                            const y = new Date().getFullYear() - offset;
                            return <option key={y} value={y}>{y}</option>;
                        })}
                    </select>

                    <button 
                        onClick={downloadPDF}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 shadow-sm transition-all"
                    >
                        <FileText className="w-4 h-4 text-red-500" /> PDF
                    </button>
                    
                    <button 
                        onClick={downloadExcel}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all"
                    >
                        <FileSpreadsheet className="w-4 h-4" /> Export Excel
                    </button>
                </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                    { label: 'Total Leads', val: data.summary.total, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
                    { label: 'Converted', val: data.summary.converted, icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
                    { label: 'Conversion Rate', val: `${data.summary.conversion_rate}%`, icon: TrendingUp, color: 'text-purple-600', bg: 'bg-purple-50' },
                    { label: 'Active Pipeline', val: data.summary.new, icon: Target, color: 'text-orange-600', bg: 'bg-orange-50' },
                ].map((item, i) => (
                    <div key={i} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-4">
                            <div className={`p-2 rounded-xl ${item.bg} ${item.color}`}>
                                <item.icon className="w-5 h-5" />
                            </div>
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{item.label}</span>
                        </div>
                        <p className="text-2xl font-black text-gray-900">{item.val}</p>
                    </div>
                ))}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Monthly Leads Bar Chart */}
                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-gray-900">Leads per Month</h3>
                        <p className="text-sm text-gray-400">Monthly acquisition trends for {year}</p>
                    </div>
                    <div className="h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.monthly}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} />
                                <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12}} />
                                <Tooltip 
                                    cursor={{fill: '#f8fafc'}}
                                    contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'}}
                                />
                                <Bar dataKey="leads" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Leads by Source Pie Chart */}
                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-gray-900">Leads by Source</h3>
                        <p className="text-sm text-gray-400">Distribution of inbound volume</p>
                    </div>
                    <div className="h-80 w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={data.sources}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                    nameKey="source"
                                >
                                    {data.sources.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip 
                                    contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)'}}
                                />
                                <Legend verticalAlign="bottom" height={36}/>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Performance Overview Table */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-8 py-6 border-b border-gray-50 flex items-center justify-between">
                    <h3 className="font-bold text-gray-900">Lead Source Breakdown</h3>
                    <span className="px-3 py-1 bg-blue-50 text-blue-600 text-xs font-bold rounded-full">Top Performers</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-gray-50/50">
                                <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Facility / Source</th>
                                <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Volume</th>
                                <th className="px-8 py-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Share</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {data.sources.map((source, i) => (
                                <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="px-8 py-4 font-semibold text-gray-800">{source.source}</td>
                                    <td className="px-8 py-4 text-gray-600">{source.value} leads</td>
                                    <td className="px-8 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-blue-500 rounded-full" 
                                                    style={{ width: `${(source.value / data.summary.total) * 100}%` }}
                                                />
                                            </div>
                                            <span className="text-xs font-bold text-gray-400">
                                                {Math.round((source.value / data.summary.total) * 100)}%
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            
            <p className="text-center text-xs text-gray-400 pb-8">
                &copy; Westlake Village Hospice CRM Reporting Engine &bull; Confidential Data
            </p>
        </div>
    );
}
